import React, { useState, useEffect } from 'react';
import useWindowDimensions from 'Components/hooks/useWindowDimensions';
import AtlasMetadataClient from '@reuters-graphics/graphics-atlas-client';
import CountryStripPlot from '@reuters-graphics/chart-module-countryrankingstrips';
import D3Locale from '@reuters-graphics/d3-locale';
import { component } from './styles.module.scss';
import debounce from 'lodash/debounce';
import max from 'lodash/maxBy';
import min from 'lodash/minBy';
import numToUnits from 'Utils/numToUnits';
import { floor10 } from 'Utils/decimalAdjust';

const atlas = new AtlasMetadataClient();

const StripPlot = ({ annotation, data, content, activeMetric, locale, id, showSplitAxis = false, icon }) => {
  const chart = new CountryStripPlot();

  const chartContainer = React.createRef();
  const { width } = useWindowDimensions();
  const resize = debounce(() => {
    chart.draw();
  }, 250);

  // Draw chart
  useEffect(() => {
    const formatNumberJapanese = (num) => {
      return (num >= 1e4) ? d3Locale.format(',.3~s')(num) : d3Locale.format(',')(floor10(num, -1));
    };
    const d3Locale = new D3Locale(locale);
    const formatNumber = (num) => locale !== 'ja' ? numToUnits(num, locale) : formatNumberJapanese(num);
    const formatNumberPct = d3Locale.format('+,.1~%');

    chart
      .selection(chartContainer.current)
      .data(data)
      .props({
        locale,
        height: 134,
        dataParams: {
          key: 'key',
          value: 'value',
        },
        margin: {
          top: 4,
          right: 8,
          bottom: 64,
          left: 8,
        },
        rugProps: {
          height: width > 576 ? 18 : 26,
          rugColor: 'rgba(255, 255, 255, 0.75)',
          highlightWidth: 3,
          highlightColor: '#eec331',
          customAxisFormat: true,
          showTipMarker: false,
          activeRugZoom: 8,
          customAxisLabels: [
            {
              pos: min(data, 'value').value,
              label: content.axisMin,
            },
            {
              pos: max(data, 'value').value,
              label: content.axisMax,
            },
          ],
          showSplitAxis,
          splitAxis: {
            value: 0,
            colors: ['#74c476', '#ee665b'],
          },
          textHeight: 18,
          annotation: [annotation],
          getTooltipText: (isoAlpha2) => atlas.getCountry(isoAlpha2).translations[locale],
          tooltipNumberFormatter: (activeMetric === 'change') ? (num) => formatNumberPct(num / 100) : (num) => formatNumber(num),
        },
      })
      .draw();
  }, [chart, annotation, activeMetric, data, chartContainer, content, locale, showSplitAxis, icon]);

  // Resize chart
  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  return (
    <div className={component}>
      <h6 className='chartTitle'>{content.title} {icon}</h6>
      <div id={id} className='country-ranking-strips' ref={chartContainer} />
    </div>
  );
};

export default StripPlot;
